import { Header } from '../components/layout/Header';
import { SEO } from '../components/common/SEO';

export function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO title="Privacy Policy – WaitlistPoker" path="/privacy" />
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-sm text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString()}</p>
          
          <div className="prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 mb-4">
                PingSolve LLC. ("Company", "we", "us", or "our") operates WaitlistPoker.com ("Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.
              </p>
              <p className="text-gray-700 mb-4">
                By using our Service, you agree to the collection and use of information in accordance with this Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-lg font-medium text-gray-900 mb-3">Personal Information</h3>
              <p className="text-gray-700 mb-4">We collect the following personal information:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Name:</strong> Your full name for account identification</li>
                <li><strong>Email Address:</strong> Required for account creation and communications</li>
                <li><strong>Phone Number:</strong> Optional, used for SMS notifications and account verification</li>
                <li><strong>Poker Alias:</strong> Optional display name (max 12 characters) shown on public waitlists instead of your real name for added privacy</li>
                <li><strong>Expo Push Notification Tokens:</strong> Device tokens for mobile push notifications (iOS/Android)</li>
                <li><strong>Firebase User ID:</strong> Unique identifier for authentication and database operations</li>
                <li><strong>Account Credentials:</strong> Encrypted passwords and authentication data</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-900 mb-3">Usage Information</h3>
              <p className="text-gray-700 mb-4">We automatically collect:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Device information (IP address, browser type, operating system)</li>
                <li>Usage patterns and preferences</li>
                <li>Waitlist participation and table preferences</li>
                <li>Service interaction data</li>
                <li>Location data (if permitted)</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-900 mb-3">Cookies and Tracking Technologies</h3>
              <p className="text-gray-700 mb-4">We use cookies and similar technologies to:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Remember your preferences and settings</li>
                <li>Analyze usage patterns and improve our Service</li>
                <li>Provide personalized content and recommendations</li>
                <li>Track performance and optimize functionality</li>
                <li>Enable third-party integrations (Firebase, Google Analytics)</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-900 mb-3">Mobile App Data</h3>
              <p className="text-gray-700 mb-4">When you use our mobile app, we additionally collect:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Expo Push notification tokens for seat-ready alerts</li>
                <li>Device type and operating system version</li>
                <li>App usage and session data</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">We use your information for the following purposes:</p>
              
              <h3 className="text-lg font-medium text-gray-900 mb-3">Service Delivery</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Provide waitlist management and table availability services</li>
                <li>Send SMS notifications about table availability and waitlist updates</li>
                <li>Deliver push notifications through mobile applications</li>
                <li>Maintain and improve Service functionality</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-900 mb-3">Communications</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Send service-related emails and notifications</li>
                <li>Provide customer support and respond to inquiries</li>
                <li>Send marketing communications about promotions and new features</li>
                <li>Notify you of important Service updates</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-900 mb-3">Analytics and Business Intelligence</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Generate aggregate analytics on poker room metrics and usage patterns</li>
                <li>Analyze user behavior to improve Service features</li>
                <li>Create anonymized reports for poker room partners</li>
                <li>Monitor Service performance and identify trends</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">3.5. SMS Messaging and Consent</h2>
              <p className="text-gray-700 mb-4">
                If you provide a phone number during signup or in your profile, you consent to receive text messages (SMS) from WaitlistPoker and participating poker rooms regarding:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Waitlist status updates and seat-ready notifications</li>
                <li>Service announcements and promotional offers</li>
                <li>Account security and verification</li>
              </ul>
              
              <h3 className="text-lg font-medium text-gray-900 mb-3">SMS Terms</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Message Frequency:</strong> Varies based on waitlist activity and your preferences. You may receive multiple messages per day when actively using waitlists.</li>
                <li><strong>Message and Data Rates:</strong> Standard message and data rates may apply from your wireless carrier.</li>
                <li><strong>Opt-Out:</strong> Reply STOP to any message to opt out of SMS notifications. You can also manage SMS preferences in your profile settings by removing your phone number.</li>
                <li><strong>Help:</strong> Reply HELP to any message for assistance or contact support@pingsolveai.com</li>
                <li><strong>Carrier Liability:</strong> Carriers are not liable for delayed or undelivered messages.</li>
                <li><strong>Withdrawal of Consent:</strong> You can withdraw SMS consent at any time without affecting your ability to use the Service (push notifications and email will still be available).</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">3.6. Push Notifications</h2>
              <p className="text-gray-700 mb-4">
                With your permission, our mobile app sends push notifications for:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Seat-ready alerts when your table is available</li>
                <li>Waitlist status updates</li>
                <li>Important service announcements</li>
              </ul>
              <p className="text-gray-700 mb-4">
                You can disable push notifications at any time through your device settings (iOS: Settings &gt; Notifications &gt; WaitlistPoker; Android: App Settings &gt; Notifications).
              </p>
              <p className="text-gray-700 mb-4">
                Push notifications are delivered via Apple Push Notification Service (APNS) for iOS and Firebase Cloud Messaging (FCM) for Android. Push tokens are automatically removed when you sign out or delete your account.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Information Sharing and Disclosure</h2>
              <p className="text-gray-700 mb-4">We may share your information in the following circumstances:</p>
              
              <h3 className="text-lg font-medium text-gray-900 mb-3">Third-Party Service Providers</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Google Firebase:</strong> Authentication, Cloud Firestore database, and Cloud Functions for backend operations</li>
                <li><strong>SMS Providers:</strong> Twilio for text message delivery</li>
                <li><strong>Push Notification Services:</strong> Apple Push Notification Service (APNS) for iOS, Firebase Cloud Messaging (FCM) for Android, Expo push notification service</li>
                <li><strong>Analytics Providers:</strong> Google Analytics for usage patterns and performance monitoring</li>
              </ul>
              <p className="text-gray-700 mb-4">
                <strong>No Sale of Data:</strong> We do not sell your personal information to third parties. Data is shared only with service providers acting as processors on our behalf under strict confidentiality and security obligations.
              </p>

              <h3 className="text-lg font-medium text-gray-900 mb-3">Business Partners</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Poker Room Operators:</strong> We may share your name, poker alias (if set), phone number (if provided), and waitlist status with the specific poker room whose waitlist you join to facilitate table management</li>
                <li><strong>Aggregated Data:</strong> We may share anonymized, aggregated analytics with poker room partners for business intelligence purposes</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-900 mb-3">Legal Requirements</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Compliance with legal obligations</li>
                <li>Protection of our rights and property</li>
                <li>Prevention of fraud or illegal activities</li>
                <li>Response to valid legal requests</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">4.5. Tracking and Advertising</h2>
              <p className="text-gray-700 mb-4">
                <strong>No Cross-App Tracking:</strong> We do not track your activity across other companies' apps or websites for advertising purposes.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>No Third-Party Advertising:</strong> We do not use third-party advertising networks or display third-party ads in our Service.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>First-Party Analytics Only:</strong> We use analytics tools (Google Analytics, Firebase Analytics) solely to understand how our Service is used and to improve functionality, not for cross-app tracking or targeted advertising.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
              <p className="text-gray-700 mb-4">We implement appropriate security measures to protect your information:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure authentication and access controls</li>
                <li>Regular security audits and updates</li>
                <li>Employee training on data protection</li>
                <li>Incident response procedures</li>
              </ul>
              <p className="text-gray-700 mb-4">
                However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Your Rights and Choices</h2>
              <p className="text-gray-700 mb-4">You have the following rights regarding your personal information:</p>
              
              <h3 className="text-lg font-medium text-gray-900 mb-3">Access and Correction</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Access your personal information</li>
                <li>Correct inaccurate or incomplete information</li>
                <li>Update your account preferences</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-900 mb-3">Communication Preferences</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Opt out of marketing communications</li>
                <li>Manage SMS notification preferences</li>
                <li>Control push notification settings</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-900 mb-3">Data Deletion</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Request deletion of your account and personal information</li>
                <li>Withdraw consent for data processing</li>
                <li>Delete your account directly from the mobile app (Profile &gt; Account Actions &gt; Delete Account) or web dashboard</li>
                <li>Request expedited deletion by contacting support@pingsolveai.com</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-900 mb-3">Account Deletion Process</h3>
              <p className="text-gray-700 mb-4">
                When you request account deletion:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li>Your account is immediately disabled and you are signed out</li>
                <li>Personal data is deleted or de-identified within 30 days, except where retention is required by law</li>
                <li>Push notification tokens and SMS preferences are removed immediately</li>
                <li>Anonymized analytics data may be retained for business intelligence purposes</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Data Retention</h2>
              <p className="text-gray-700 mb-4">We retain your information for the following periods:</p>
              <ul className="list-disc pl-6 text-gray-700 mb-4">
                <li><strong>Active Accounts:</strong> Personal data retained while your account is active or as needed to provide the Service</li>
                <li><strong>Inactive Accounts:</strong> Accounts inactive for 2+ years may be deleted after notice</li>
                <li><strong>After Deletion Request:</strong> Personal data deleted or de-identified within 30 days for security, fraud prevention, and legal compliance</li>
                <li><strong>Push Tokens and SMS Preferences:</strong> Removed immediately upon sign-out or account deletion</li>
                <li><strong>Analytics Data:</strong> Aggregated, anonymized data may be retained indefinitely</li>
                <li><strong>Legal Requirements:</strong> Data retained as required by applicable law (e.g., financial records, legal holds)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Children's Privacy</h2>
              <p className="text-gray-700 mb-4">
                Our Service is not directed to individuals under 13 years of age (or under 16 in certain jurisdictions). We do not knowingly collect personal information from children. The Service is designed for adults aged 21+ due to its association with poker rooms and gaming establishments. If we become aware that we have collected personal information from a child under the applicable age, we will take steps to delete such information promptly. Parents or guardians who believe we have collected information from a child should contact us immediately at support@pingsolveai.com.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">9. International Users</h2>
              <p className="text-gray-700 mb-4">
                If you are accessing our Service from outside the United States, please be aware that your information may be transferred to, stored, and processed in the United States. By using our Service, you consent to the transfer of your information to the United States.
              </p>
              <p className="text-gray-700 mb-4">
                Where we transfer data internationally, we rely on appropriate safeguards such as Standard Contractual Clauses (SCCs) or other legally recognized transfer mechanisms to protect your information in accordance with applicable data protection laws.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Email:</strong> support@pingsolveai.com<br />
                <strong>Company:</strong> PingSolve LLC.<br />
                <strong>Service:</strong> WaitlistPoker.com
              </p>
              <p className="text-gray-700 mb-4">
                <strong>For Privacy Requests:</strong> To exercise your privacy rights (access, correction, deletion), please email support@pingsolveai.com with "Privacy Request" in the subject line. We will respond within 30 days.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
